import { Accordion, Button, useAccordionButton, Card, OverlayTrigger, Tooltip, ListGroup } from 'react-bootstrap';
import { React, useState, useEffect } from 'react';
import { ApiManager } from '../../ApiManager';
import { handleApiError } from '../../utils/apiErrorHandler';
import Strategies from './Strategies';
import StrategyModal from "../Modals/StrategyModal";
import ConfirmationModal from '../Modals/ConfirmationModal';
import ChapterModal from '../Modals/ChapterModal';
import PencilIcon from '../../Assets/Images/pencil-square.svg'
import TrashIcon from '../../Assets/Images/trash.svg'
import AddIcon from '../../Assets/Images/plus-circle.svg'
import { computeCompliance, BAND, BAND_COLORS, BAND_TEXT_COLORS } from '../../utils/complianceUtils';

const Chapters = (props) => {
    const project = props.project;
    const [chapter, setChapter] = useState(props.chapter);
    const setReloadChapter = props.setReloadChapter;
    const setReloadSection = props.setReloadSection;
    const isEditingSection = props.isEditingSection;
    const reloadChapter = props.reloadChapter;
    const editingProject = props.editingProject;
    const section = props.section;

    const [enableChapterHandler, setEnableChapterHandler] = useState(false);
    const [showChapterModal, setShowChapterModal] = useState(false);
    const [showStrategyModal, setShowStrategyModal] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    // `trailing` renders outside the truncating span and never shrinks — the compliance %
    // used to sit inside it, so a long chapter name ellipsised the score away entirely
    function CustomHeader({ children, eventKey, trailing }) {
        const customHeaderClicked = useAccordionButton(eventKey);

        return (
            <>
                <span
                    style={{ cursor: "pointer", textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden", flex: 1, minWidth: 0 }}
                    onClick={customHeaderClicked}>{children}</span>
                {trailing &&
                    <span className="ms-2 flex-shrink-0" style={{ cursor: "pointer" }} onClick={customHeaderClicked}>{trailing}</span>}
                {
                    isEditingSection &&
                    <div className="ms-auto">
                        {!editingProject && <OverlayTrigger placement="top" overlay={<Tooltip id={"tooltip-chapters-add"}>Add Strategy to Chapter</Tooltip>}>
                            <Button className="icon-btn" onClick={() => setShowStrategyModal(true)}><img src={AddIcon} alt="Add Icon" /></Button>
                        </OverlayTrigger>}
                        <OverlayTrigger placement="top" overlay={<Tooltip id={"tooltip-chapters-edit"}>Edit Chapter</Tooltip>}>
                            <Button className="icon-btn" onClick={() => setShowChapterModal(true)}><img src={PencilIcon} alt="Pencil Icon" /></Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip id={"tooltip-sections-remove"}>Remove Chapter</Tooltip>}>
                            <Button className="icon-btn" onClick={() => setShowConfirmationModal(true)}><img src={TrashIcon} alt="Trash Icon" /></Button>
                        </OverlayTrigger>
                    </div>
                }
            </>
        );
    }

    useEffect(() => {
        setChapter(props.chapter);
    }, [props.chapter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (reloadChapter) {
            const apiCall = editingProject
                ? ApiManager.getInstance().getProjectChapterById(chapter)
                : ApiManager.getInstance().getChapterById(chapter);
            apiCall.then((result) => {
                setChapter(result);
            }).catch(handleApiError).finally(() => {
                // Reset even on failure — a stuck-true flag makes every later
                // setReloadChapter(true) a silent no-op for this chapter
                setReloadChapter(false);
            });
        }
    }, [reloadChapter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (enableChapterHandler) {
            const updatedChapter = { ...chapter, Enabled: !chapter.Enabled };
            const apiCall = editingProject
                ? ApiManager.getInstance().deleteProjectChapter(updatedChapter)
                : ApiManager.getInstance().deleteChapter(updatedChapter);
            apiCall.then(() => {
                setReloadSection(true);
            }).catch(handleApiError).finally(() => {
                setEnableChapterHandler(false);
            });
        }
    }, [enableChapterHandler]); // eslint-disable-line react-hooks/exhaustive-deps

    // Prerequisites float to the top (stable sort keeps DB order within each group);
    // banding only applies project-side — admin catalog strategies have no Complies data,
    // so computeCompliance yields band NONE there anyway.
    const chapterStats = computeCompliance(chapter.Strategies);
    const sortedStrategies = [...chapter.Strategies].sort((a, b) => (b.IsPrerequisite === true) - (a.IsPrerequisite === true));

    return (
        <>
            <ChapterModal
                chapter={chapter}
                editingProject={editingProject}
                createOrUpdate={2}
                section={section}
                showChapterModal={showChapterModal}
                setShowChapterModal={setShowChapterModal}
                setReloadSection={setReloadSection} />
            <StrategyModal
                editingProject={editingProject}
                createOrUpdate={1}
                chapter={chapter}
                showStrategyModal={showStrategyModal}
                setShowStrategyModal={setShowStrategyModal}
                setReloadChapter={setReloadChapter} />
            <ConfirmationModal
                type="Chapter"
                setYesHandler={setEnableChapterHandler}
                showConfirmationModal={showConfirmationModal}
                setShowConfirmationModal={setShowConfirmationModal}
                Name={chapter.Name} />
            {chapter.Enabled &&
                <Card key={chapter.Id}>
                    <Card.Header style={{ display: "flex", ...(editingProject && chapterStats.band !== BAND.NONE ? { backgroundColor: BAND_COLORS[chapterStats.band], color: BAND_TEXT_COLORS[chapterStats.band] } : {}) }}>
                        <CustomHeader
                            chapter={chapter}
                            eventKey={chapter.Id}
                            trailing={editingProject && chapterStats.percent !== null
                                ? <span className="fw-bold">{Math.round(chapterStats.percent)}%</span>
                                : null}>
                            {chapter.SectionNumber}.{chapter.Number} {chapter.Name}
                        </CustomHeader>
                    </Card.Header>
                    <Accordion.Collapse eventKey={chapter.Id}>
                        <Card.Body>
                            {chapter.Strategies.filter(strat => strat.Enabled).length < 1 ? <p>No Strategies Assigned</p> :
                                sortedStrategies.map((strat) =>
                                    <ListGroup key={strat.Id}>
                                        <Strategies
                                            key={strat.Id}
                                            strategy={strat}
                                            chapter={chapter}
                                            editingProject={editingProject}
                                            IsEditingSection={isEditingSection}
                                            setReloadChapter={setReloadChapter}
                                            project={project}
                                        />
                                    </ListGroup>
                                )}
                        </Card.Body>
                    </Accordion.Collapse>
                </Card>}
        </>
    );
};

export default Chapters;